import {onScopeDispose, ref, type Ref} from 'vue';

/** What a caller must supply to {@link useIntersectionLoader}. */
export interface UseIntersectionLoaderOptions {
  /** Called each time the sentinel comes into range. */
  onReach(): void;
  /**
   * CSS selector for the element that actually scrolls. Left unset, the observer falls back to the
   * viewport — which silently disables `rootMargin` whenever an ancestor clips the sentinel.
   */
  rootSelector?: string;
  rootMargin?: string;
  /** Called when `rootSelector` matches nothing, so the fallback is reported rather than silent. */
  onMissingRoot?(): void;
}

/** The sentinel and observer controls {@link useIntersectionLoader} returns. */
export interface UseIntersectionLoader {
  /** Bind to the element placed after the list. */
  sentinel: Ref<HTMLElement | null>;
  /** Build the observer. Independent of the sentinel, which may not be rendered yet. */
  create(): void;
  /** Watch the sentinel currently in the DOM. Safe to call repeatedly. */
  observe(): void;
  /** Stop watching, keeping the observer for later. The counterpart to {@link observe}. */
  pause(): void;
  disconnect(): void;
}

/**
 * Watches a sentinel and reports when the reader has scrolled within reach of it.
 *
 * Two things here are less obvious than they look:
 *
 * `rootSelector` matters. An ancestor's overflow clip is applied to the target before the result is
 * intersected with the root, so against the default viewport root a `rootMargin` is expanded on a
 * rect the clip has already cut back — the margin does nothing and the sentinel only fires once it
 * is genuinely on screen. Naming the element that scrolls is what makes the margin real.
 *
 * `rearm` is not just re-arming. An element that stays intersecting produces no further callbacks,
 * so a page that did not reach past the fold would never ask for the next one; and a list that
 * re-renders replaces the sentinel node, leaving the observer holding a detached one. Disconnecting
 * and re-observing the current node covers both.
 */
export function useIntersectionLoader({
  onReach,
  rootSelector,
  rootMargin = '400px',
  onMissingRoot,
}: UseIntersectionLoaderOptions): UseIntersectionLoader {
  const sentinel = ref<HTMLElement | null>(null);
  let observer: IntersectionObserver | null = null;

  const create = (): void => {
    // A second create() would otherwise strand the first observer still holding its targets and
    // still calling onReach, silently doubling every trigger.
    observer?.disconnect();
    const root = rootSelector ? document.querySelector(rootSelector) : null;
    if (rootSelector && !root) {
      onMissingRoot?.();
    }
    observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) {
        onReach();
      }
    }, {root, rootMargin});
  };

  const pause = (): void => {
    observer?.disconnect();
  };

  const observe = (): void => {
    if (!observer || !sentinel.value) {
      return;
    }
    observer.disconnect();
    observer.observe(sentinel.value);
  };

  const disconnect = (): void => {
    observer?.disconnect();
    observer = null;
  };

  // The composable owns a live DOM observer, so it cleans up with its own scope rather than
  // relying on every caller to remember.
  onScopeDispose(disconnect);

  return {sentinel, create, observe, pause, disconnect};
}
